import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { DollarSign, Package, Users, TrendingUp, Copy } from "lucide-react";
import { toast, Toaster } from "sonner";
import { BRAND } from "@/config/brand";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user) api.get("/auth/dashboard").then((r) => setData(r.data));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/signin" replace />;

  const s = data?.stats || {};
  return (
    <SiteLayout>
      <Toaster richColors position="top-center" />
      <section className="section-py container-x">
        <div className="flex flex-wrap justify-between items-start gap-6">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight">Hi {user.full_name || user.email},</h1>
            <p className="mt-2 text-muted-foreground">Here's a snapshot of your account.</p>
          </div>
          <Link to="/submit" className="btn-pill text-sm" data-testid="dashboard-new-submit">Sell another card</Link>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: DollarSign, label: "Total earned", value: `$${(s.total_earned||0).toFixed(2)}` },
            { icon: Package, label: "Submissions", value: s.total_submissions || 0 },
            { icon: TrendingUp, label: "Pending", value: s.pending || 0 },
            { icon: Users, label: "Referrals", value: s.referrals_count || 0 },
          ].map((k, i) => (
            <div key={i} className="card-soft" data-testid={`dash-kpi-${i}`}>
              <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><k.icon size={20} /></div>
              <div className="text-3xl font-extrabold mt-4">{k.value}</div>
              <div className="text-sm text-muted-foreground">{k.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-soft">
            <h3 className="font-extrabold text-xl mb-4">Recent submissions</h3>
            {data?.submissions?.length ? (
              <div className="divide-y divide-border">
                {data.submissions.map((sub) => (
                  <div key={sub.id} className="py-4 flex justify-between items-center gap-4" data-testid={`dash-sub-${sub.reference}`}>
                    <div className="min-w-0">
                      <div className="font-extrabold truncate">{sub.brand_name} · ${sub.card_amount}</div>
                      <div className="text-xs text-muted-foreground font-mono">{sub.reference}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase font-bold text-muted-foreground">{sub.status.replace("_"," ")}</div>
                      <div className="text-sm font-bold">${(sub.final_payout||sub.estimated_payout||0).toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-muted-foreground">No submissions yet. <Link to="/submit" className="text-primary underline font-bold">Sell your first card →</Link></div>
            )}
          </div>

          <div className="card-soft">
            <h3 className="font-extrabold text-xl">Your referral code</h3>
            <div className="mt-4 p-4 rounded-2xl bg-primary text-primary-foreground flex justify-between items-center">
              <span className="font-mono font-extrabold">{user.referral_code}</span>
              <button onClick={() => { navigator.clipboard.writeText(user.referral_code); toast.success("Copied"); }} data-testid="dash-copy-ref"><Copy size={16} /></button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Give this to friends. You get ${BRAND.referralBonusReferrer} when they first sell. They get ${BRAND.referralBonusNew}.</p>
            <div className="mt-4 text-sm font-bold">Wallet credit: <span className="text-primary">${(user.balance_credit||0).toFixed(2)}</span></div>
            <Link to="/refer" className="btn-pill-outline mt-4 text-xs inline-flex">Refer & earn</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
